import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import SchoolSidebar from './SchoolSidebar';
import { SchoolMobileHeader } from './SchoolMobileHeader';
import { useBranchFilter } from '../../hooks/useBranchFilter';
import './SchoolDashboard.css';

function AddTeacher() {
  const navigate = useNavigate();
  const [schoolData, setSchoolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { selectedBranchId } = useBranchFilter();
  
  // Add Teacher form state
  const [teacherForm, setTeacherForm] = useState({
    firstName: '',
    middleName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    dateOfBirth: '',
    phone: '',
    address: '',
    country: 'Kenya',
    city: '',
    postalCode: '',
    qualification: '',
    experience: '',
    salary: '',
    joiningDate: '',
    subjects: [''], // Start with one empty subject
    emergencyContact: '',
    emergencyPhone: '',
    nationalId: '',
    employeeId: '',
    profilePicture: null
  });
  const [profilePicturePreview, setProfilePicturePreview] = useState(null);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [submissionError, setSubmissionError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Password visibility state
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Country code states
  const [teacherPhoneCountryCode, setTeacherPhoneCountryCode] = useState('+254'); // Default: Kenya
  const [teacherEmergencyPhoneCountryCode, setTeacherEmergencyPhoneCountryCode] = useState('+254'); // Default: Kenya
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successData, setSuccessData] = useState(null);
  
  // Subjects from curriculum form
  const [availableSubjects, setAvailableSubjects] = useState([]);
  const [loadingSubjects, setLoadingSubjects] = useState(false);
  
  // Branch state
  const [branches, setBranches] = useState([]);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [formBranchId, setFormBranchId] = useState(null); // Branch selected in form when "All Branches" is selected
  
  // Ref for file input to allow resetting value
  const fileInputRef = useRef(null);

  useEffect(() => {
    // Get school data from localStorage
    const storedSchoolData = localStorage.getItem('schoolData');
    if (storedSchoolData) {
      setSchoolData(JSON.parse(storedSchoolData));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (schoolData?.id) {
      fetchDistinctSubjects();
    }
  }, [schoolData?.id, selectedBranchId]);

  // Fetch branches when school data is available
  useEffect(() => {
    const fetchBranches = async () => {
      if (!schoolData?.id) return;
      
      try {
        setLoadingBranches(true);
        const { data, error } = await supabase
          .from('branches')
          .select('*')
          .eq('school_id', schoolData.id)
          .eq('is_active', true)
          .order('branch_name');
        
        if (error) {
          console.error('Error fetching branches:', error);
          setBranches([]);
        } else {
          setBranches(data || []);
        }
      } catch (err) {
        console.error('Error:', err);
        setBranches([]);
      } finally {
        setLoadingBranches(false);
      }
    };

    if (schoolData?.id) {
      fetchBranches();
    }
  }, [schoolData?.id]);

  const fetchDistinctSubjects = async () => {
    if (!schoolData?.id) return;

    setLoadingSubjects(true);
    try {
      // Fetch all subjects for this school
      let subjectsQuery = supabase
        .from('subjects')
        .select('subject_name')
        .eq('school_id', schoolData.id);

      if (selectedBranchId) {
        subjectsQuery = subjectsQuery.eq('branch_id', selectedBranchId);
      }

      const { data: subjectsData, error: subjectsError } = await subjectsQuery;

      if (subjectsError) throw subjectsError;

      // Get distinct subject names (case-insensitive)
      const subjectNames = (subjectsData || []).map(s => s.subject_name);
      const distinctSubjects = [...new Set(subjectNames.map(name => name.toLowerCase()))]
        .map(lowerName => {
          // Find the original case version
          return subjectNames.find(name => name.toLowerCase() === lowerName);
        })
        .filter(Boolean)
        .sort();

      setAvailableSubjects(distinctSubjects);
    } catch (error) {
      console.error('Error fetching subjects:', error);
    } finally {
      setLoadingSubjects(false);
    }
  };


  const handleLogout = () => {
    navigate('/schoollogin');
  };

  // Add Teacher form functions
  // Generate password function: word + numbers (minimum 6 characters total)
  const generatePassword = () => {
    const words = ['book', 'star', 'moon', 'tree', 'bird', 'fish', 'rock', 'wind', 'fire', 'wave', 'lake', 'hill', 'rose', 'leaf', 'snow', 'rain', 'sun', 'sky', 'sea', 'gem'];
    const randomWord = words[Math.floor(Math.random() * words.length)];
    const wordLength = randomWord.length;
    
    // Calculate how many digits we need to ensure minimum 6 characters total
    // If word is 3 chars, need 3 digits (total 6)
    // If word is 4 chars, need 2 digits (total 6)
    // If word is 5+ chars, need 1 digit (total 6+)
    const minDigitsNeeded = Math.max(1, 6 - wordLength);
    
    // Generate random number with appropriate number of digits
    // For 1 digit: 0-9
    // For 2 digits: 10-99
    // For 3 digits: 100-999
    const maxNumber = Math.pow(10, minDigitsNeeded) - 1;
    const minNumber = minDigitsNeeded === 1 ? 0 : Math.pow(10, minDigitsNeeded - 1);
    const randomNumber = Math.floor(Math.random() * (maxNumber - minNumber + 1)) + minNumber;
    const randomDigits = randomNumber.toString().padStart(minDigitsNeeded, '0');
    
    return randomWord + randomDigits;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setTeacherForm(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    // Clear submission error when user starts typing
    if (submissionError) {
      setSubmissionError('');
    }
  };

  const handleProfilePictureChange = async (e) => {
    const file = e.target.files[0];
    if (!file) {
      setProfilePicturePreview(null);
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      setProfilePicturePreview(null);
      e.target.value = '';
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size should be less than 5MB');
      setProfilePicturePreview(null);
      e.target.value = '';
      return;
    }

    // Update form with file
    setTeacherForm(prev => ({
      ...prev,
      profilePicture: file
    }));

    // Create preview immediately
    const reader = new FileReader();
    reader.onloadend = () => {
      if (reader.result) {
        setProfilePicturePreview(reader.result);
      } else {
        console.error('Failed to create preview');
        setProfilePicturePreview(null);
      }
    };
    reader.onerror = () => {
      console.error('Error reading file for preview');
      setProfilePicturePreview(null);
    };
    reader.readAsDataURL(file);
  };

  const removeProfilePicture = () => {
    setTeacherForm(prev => ({
      ...prev,
      profilePicture: null
    }));
    setProfilePicturePreview(null);
    // Reset file input using ref
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubjectChange = (index, value) => {
    setTeacherForm(prev => ({
      ...prev,
      subjects: prev.subjects.map((subject, i) => 
        i === index ? value : subject
      )
    }));
    
    // Clear subject errors when user starts typing
    if (formErrors[`subject_${index}`]) {
      setFormErrors(prev => ({
        ...prev,
        [`subject_${index}`]: ''
      }));
    }
    // Clear submission error when user starts typing
    if (submissionError) {
      setSubmissionError('');
    }
  };

  const addSubject = () => {
    setTeacherForm(prev => ({
      ...prev,
      subjects: [...prev.subjects, '']
    }));
  };

  const removeSubject = (index) => {
    if (teacherForm.subjects.length > 1) {
      setTeacherForm(prev => ({
        ...prev,
        subjects: prev.subjects.filter((_, i) => i !== index)
      }));
      
      // Clear error for removed subject
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`subject_${index}`];
        return newErrors;
      });
    }
  };


  const validateForm = () => {
    const errors = {};
    
    if (!teacherForm.firstName.trim()) {
      errors.firstName = 'First name is required';
    }
    
    if (!teacherForm.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }
    
    if (!teacherForm.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(teacherForm.email)) {
      errors.email = 'Email is invalid';
    }
    
    if (!teacherForm.password.trim()) {
      errors.password = 'Password is required';
    }
    
    if (!teacherForm.confirmPassword.trim()) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (teacherForm.password !== teacherForm.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    if (!teacherForm.dateOfBirth.trim()) {
      errors.dateOfBirth = 'Date of birth is required';
    }
    
    if (!teacherForm.phone.trim()) {
      errors.phone = 'Phone number is required';
    }
    
    if (!teacherForm.address.trim()) {
      errors.address = 'Address is required';
    }
    
    if (!teacherForm.country.trim()) {
      errors.country = 'Country is required';
    }
    
    if (!teacherForm.city.trim()) {
      errors.city = 'City/Town is required';
    }
    
    if (!teacherForm.postalCode.trim()) {
      errors.postalCode = 'Postal Code is required';
    }
    
    if (!teacherForm.qualification.trim()) {
      errors.qualification = 'Qualification is required';
    }
    
    if (!teacherForm.experience.trim()) {
      errors.experience = 'Experience is required';
    } else if (isNaN(teacherForm.experience) || parseInt(teacherForm.experience) < 0) {
      errors.experience = 'Experience must be a valid number';
    }
    
    if (!teacherForm.salary.trim()) {
      errors.salary = 'Salary is required';
    } else if (isNaN(teacherForm.salary) || parseFloat(teacherForm.salary) <= 0) {
      errors.salary = 'Salary must be a valid positive number';
    }
    
    if (!teacherForm.joiningDate.trim()) {
      errors.joiningDate = 'Joining date is required';
    }
    
    // Validate branch selection when "All Branches" is selected
    if (!selectedBranchId && branches.length > 0 && !formBranchId) {
      errors.branchId = 'Branch selection is required';
    }
    
    // Validate subjects
    const validSubjects = teacherForm.subjects.filter(subject => subject.trim() !== '');
    if (validSubjects.length === 0) {
      errors.subjects = 'At least one subject is required';
    }
    
    // Check for duplicate subjects
    const subjectSet = new Set();
    teacherForm.subjects.forEach((subject, index) => {
      if (subject.trim() !== '') {
        if (subjectSet.has(subject.trim().toLowerCase())) {
          errors[`subject_${index}`] = 'Duplicate subject';
        } else {
          subjectSet.add(subject.trim().toLowerCase());
        }
      }
    });
    
    if (!teacherForm.emergencyContact.trim()) {
      errors.emergencyContact = 'Emergency contact is required';
    }
    
    if (!teacherForm.emergencyPhone.trim()) {
      errors.emergencyPhone = 'Emergency phone is required';
    }
    
    // Validate National/Government ID if provided
    if (teacherForm.nationalId.trim()) {
      // National ID is provided, will check uniqueness in handleSubmit
    }
    
    setFormErrors(errors);
    const firstErrorMessage = Object.values(errors)[0] || '';
    if (firstErrorMessage) {
      setSubmissionError(firstErrorMessage);
    } else {
      setSubmissionError('');
    }
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Check if National/Government ID already exists (if provided)
      if (teacherForm.nationalId.trim()) {
        const { data: existingTeacher, error: checkError } = await supabase
          .from('teachers')
          .select('id, first_name, last_name, national_id')
          .eq('school_id', schoolData.id)
          .eq('national_id', teacherForm.nationalId.trim())
          .maybeSingle();
        
        if (checkError) {
          console.error('Error checking National ID:', checkError);
          alert('Error checking National ID. Please try again.');
          setIsSubmitting(false);
          return;
        }
        
        if (existingTeacher) {
          const existingName = `${existingTeacher.first_name} ${existingTeacher.last_name}`;
          setFormErrors({
            nationalId: `A teacher with this National/Government ID already exists: ${existingName}`
          });
          setSubmissionError(`A teacher with National/Government ID "${teacherForm.nationalId.trim()}" already exists: ${existingName}`);
          setIsSubmitting(false);
          return;
        }
      }
      
      // Email strictness removed - allow same email for different user types
      const existingTeacherId = null;
      
      // Hash password for storage in teachers table (using Base64 like schools)
      const passwordHash = btoa(teacherForm.password.trim());
      console.log('Password hash created for teacher:', teacherForm.email);
      
      // Upload profile picture if provided
      let profilePictureUrl = null;
      if (teacherForm.profilePicture) {
        try {
          setUploadingPicture(true);
          const fileExt = teacherForm.profilePicture.name.split('.').pop();
          const fileName = `teacher_${Date.now()}.${fileExt}`;
          const filePath = `teacher-profiles/${schoolData.id}/${fileName}`;

          const { error: uploadError } = await supabase.storage
            .from('teacher-uploads')
            .upload(filePath, teacherForm.profilePicture, {
              cacheControl: '3600',
              upsert: false
            });

          if (uploadError) {
            console.error('Error uploading profile picture:', uploadError);
            // Continue without profile picture if upload fails
          } else {
            // Get public URL
            const { data: urlData } = supabase.storage
              .from('teacher-uploads')
              .getPublicUrl(filePath);
            profilePictureUrl = urlData.publicUrl;
          }
        } catch (uploadErr) {
          console.error('Error uploading profile picture:', uploadErr);
          // Continue without profile picture if upload fails
        } finally {
          setUploadingPicture(false);
        }
      }
      
      // Filter out empty subjects and create teacher data
      const validSubjects = teacherForm.subjects.filter(subject => subject.trim() !== '');
      
      // Use formBranchId if available, otherwise fallback to selectedBranchId
      const finalBranchId = formBranchId || selectedBranchId || null;
      
      const teacherData = {
        school_id: schoolData.id,
        branch_id: finalBranchId, // Include branch_id if a branch is selected
        password_hash: passwordHash, // Store password hash in teachers table
        first_name: teacherForm.firstName.trim(),
        middle_name: teacherForm.middleName.trim() || null,
        last_name: teacherForm.lastName.trim(),
        email: teacherForm.email.trim(),
        date_of_birth: teacherForm.dateOfBirth,
        phone: teacherForm.phone.trim() ? `${teacherPhoneCountryCode} ${teacherForm.phone.trim()}` : '',
        address: teacherForm.address.trim(),
        country: teacherForm.country.trim(),
        city: teacherForm.city.trim(),
        postal_code: teacherForm.postalCode.trim(),
        qualification: teacherForm.qualification.trim(),
        experience: parseInt(teacherForm.experience),
        salary: parseFloat(teacherForm.salary),
        joining_date: teacherForm.joiningDate,
        subjects: validSubjects, // Store as array
        emergency_contact: teacherForm.emergencyContact.trim(),
        emergency_phone: teacherForm.emergencyPhone.trim() ? `${teacherEmergencyPhoneCountryCode} ${teacherForm.emergencyPhone.trim()}` : '',
        national_id: teacherForm.nationalId.trim() || null,
        employee_id: teacherForm.employeeId.trim() || null,
        profile_picture_url: profilePictureUrl,
        is_active: true,
        created_at: new Date().toISOString()
      };
      
      console.log('Saving teacher data to Supabase:', teacherData);
      
      let data, error;
      
      // If there's an existing incomplete record, update it instead of inserting
      if (existingTeacherId) {
        console.log('Updating existing incomplete teacher record:', existingTeacherId);
        // Remove created_at from update data
        const { created_at, ...updateData } = teacherData;
        const result = await supabase
          .from('teachers')
          .update(updateData)
          .eq('id', existingTeacherId)
          .select()
          .single();
        data = result.data;
        error = result.error;
      } else {
        // Insert new teacher record
        const result = await supabase
          .from('teachers')
          .insert(teacherData)
          .select()
          .single();
        data = result.data;
        error = result.error;
      }
      
      if (error) {
        console.error('Error saving teacher:', error);
        if (error.code === '23505') {
          // Unique constraint violation - check if it's for this school
          // Email strictness removed - allow same email for different user types
          // Just show generic error message
          console.log('Error saving teacher (email may already exist, but that\'s allowed):', error.message);
        } else {
          alert(`Failed to add teacher: ${error.message}`);
        }
        setIsSubmitting(false);
        return;
      }
      
      console.log('Teacher added successfully:', data);
      console.log('Teacher can now login with email:', teacherForm.email, 'and password');
      
      // Reset form
      setTeacherForm({
        firstName: '',
        middleName: '',
        lastName: '',
        email: '',
        password: '',
        confirmPassword: '',
        dateOfBirth: '',
        phone: '',
        address: '',
        country: 'Kenya',
        city: '',
        postalCode: '',
        qualification: '',
        experience: '',
        salary: '',
        joiningDate: '',
        subjects: [''],
        emergencyContact: '',
        emergencyPhone: '',
        nationalId: '',
        employeeId: '',
        profilePicture: null
      });
      setProfilePicturePreview(null);
      setFormErrors({});
      setFormBranchId(null); // Reset branch selection
      
      // Show success popup
      setSuccessData({
        email: teacherForm.email,
        password: teacherForm.password,
        name: `${teacherForm.firstName} ${teacherForm.lastName}`
      });
      setShowSuccessPopup(true);
      
    } catch (err) {
      console.error('Error:', err);
      alert('Failed to add teacher. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="school-dashboard">
        <div className="loading-container">
          <div className="loading-spinner">⏳</div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="school-dashboard">
      <SchoolMobileHeader 
        schoolData={schoolData} 
        mobileMenuOpen={mobileMenuOpen} 
        setMobileMenuOpen={setMobileMenuOpen} 
        handleLogout={handleLogout} 
      />
      
      {/* Sidebar */}
      <SchoolSidebar schoolData={schoolData} onLogout={handleLogout} />

      {/* Main Content */}
      <div className="main-content">
        <div className="content-header">
          <h1>Add New Teacher</h1>
          <div className="user-info">
            <span>{schoolData?.school_name || 'School'}</span>
            <div className="user-avatar">🏫</div>
          </div>
        </div>
        
        <div className="content-body">
          <div className="dashboard-content">
            <div className="add-teacher-section">
              <div className="add-teacher-card">
                <form onSubmit={handleSubmit} className="add-teacher-form">
                  {submissionError && (
                    <div className="submission-error-summary" role="alert">
                      {submissionError}
                    </div>
                  )}
                  <div className="form-section">
                    <h3>Teacher Information</h3>
                    {/* Show branch selection when "All Branches" is selected and other branches exist */}
                    {!selectedBranchId && branches.length > 0 && (
                      <div className="form-row">
                        <div className="form-group">
                          <label htmlFor="formBranchId">Branch *</label>
                          <select
                            id="formBranchId"
                            name="formBranchId"
                            value={formBranchId || ''}
                            onChange={(e) => setFormBranchId(e.target.value ? parseInt(e.target.value) : null)}
                            className={formErrors.branchId ? 'error' : ''}
                          >
                            <option value="">Select Branch</option>
                            {branches.map((branch) => (
                              <option key={branch.id} value={branch.id}>
                                {branch.branch_name}
                              </option>
                            ))}
                          </select>
                          {formErrors.branchId && (
                            <span className="error-text">{formErrors.branchId}</span>
                          )}
                          <small className="form-help">Teachers cannot be added to "All Branches". Please select a specific branch.</small>
                        </div>
                      </div>
                    )}
                    <div className="form-row three-columns">
                      <div className="form-group">
                        <label htmlFor="firstName">First Name *</label>
                        <input
                          type="text"
                          id="firstName"
                          name="firstName"
                          value={teacherForm.firstName}
                          onChange={handleInputChange}
                          className={formErrors.firstName ? 'error' : ''}
                          placeholder="Enter first name"
                        />
                        {formErrors.firstName && (
                          <span className="error-text">{formErrors.firstName}</span>
                        )}
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="middleName">Middle Name</label>
                        <input
                          type="text"
                          id="middleName"
                          name="middleName"
                          value={teacherForm.middleName}
                          onChange={handleInputChange}
                          placeholder="Enter middle name (optional)"
                        />
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="lastName">Last Name *</label>
                        <input
                          type="text"
                          id="lastName"
                          name="lastName"
                          value={teacherForm.lastName}
                          onChange={handleInputChange}
                          className={formErrors.lastName ? 'error' : ''}
                          placeholder="Enter last name"
                        />
                        {formErrors.lastName && (
                          <span className="error-text">{formErrors.lastName}</span>
                        )}
                      </div>
                    </div>
                    
                    <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', alignItems: 'start' }}>
                      <div className="form-group" style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
                          Profile Picture
                        </label>
                        <div style={{
                          position: 'relative',
                          marginTop: '0.5rem',
                          width: '100%'
                        }}>
                          <input
                            ref={fileInputRef}
                            type="file"
                            id="profilePicture"
                            name="profilePicture"
                            accept="image/*"
                            onChange={handleProfilePictureChange}
                            style={{
                              position: 'absolute',
                              width: '0.1px',
                              height: '0.1px',
                              opacity: 0,
                              overflow: 'hidden',
                              zIndex: -1
                            }}
                          />
                          <label
                            htmlFor="profilePicture"
                            onClick={(e) => {
                              // Reset input value when label is clicked to allow selecting the same file again
                              // This is done before the file dialog opens, so onChange will fire
                              if (fileInputRef.current) {
                                fileInputRef.current.value = '';
                              }
                            }}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              padding: '0.5rem 1rem',
                              background: '#FFFFFF',
                              color: '#374151',
                              border: '1px solid #D1D5DB',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '0.875rem',
                              fontWeight: '400',
                              transition: 'all 0.2s ease',
                              width: '100%',
                              textAlign: 'center',
                              gap: '0.5rem',
                              userSelect: 'none',
                              boxSizing: 'border-box'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = '#F9FAFB';
                              e.currentTarget.style.borderColor = '#9CA3AF';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = '#FFFFFF';
                              e.currentTarget.style.borderColor = '#D1D5DB';
                            }}
                          >
                            <span style={{ fontSize: '0.9rem' }}>📷</span>
                            <span>{teacherForm.profilePicture ? 'Change' : 'Choose File'}</span>
                          </label>
                        </div>
                        {teacherForm.profilePicture && (
                          <div style={{
                            marginTop: '1rem',
                            padding: '1rem',
                            background: '#F9FAFB',
                            borderRadius: '8px',
                            border: '1px solid #E5E7EB',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '1rem',
                            width: '100%',
                            boxSizing: 'border-box'
                          }}>
                            <div style={{
                              width: '80px',
                              height: '80px',
                              borderRadius: '50%',
                              overflow: 'hidden',
                              border: '3px solid #FF8C42',
                              flexShrink: 0,
                              position: 'relative',
                              background: '#E5E7EB'
                            }}>
                              {profilePicturePreview ? (
                                <img 
                                  src={profilePicturePreview} 
                                  alt="Profile preview" 
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    display: 'block'
                                  }}
                                  onLoad={() => {
                                    console.log('Profile picture preview loaded successfully');
                                  }}
                                  onError={(e) => {
                                    console.error('Error loading preview image');
                                    e.target.style.display = 'none';
                                  }}
                                />
                              ) : (
                                <div style={{
                                  width: '100%',
                                  height: '100%',
                                  background: '#E5E7EB',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '2rem',
                                  position: 'absolute',
                                  top: 0,
                                  left: 0
                                }}>
                                  👤
                                </div>
                              )}
                            </div>
                            <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                              <div style={{ 
                                fontWeight: '600', 
                                color: '#1F2937', 
                                marginBottom: '0.25rem', 
                                wordBreak: 'break-word',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}>
                                {teacherForm.profilePicture.name}
                              </div>
                              <div style={{ fontSize: '0.85rem', color: '#6B7280', whiteSpace: 'nowrap' }}>
                                {(teacherForm.profilePicture.size / 1024 / 1024).toFixed(2)} MB
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={removeProfilePicture}
                              style={{
                                padding: '0.4rem 0.6rem',
                                background: '#EF4444',
                                color: '#FFFFFF',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.8rem',
                                fontWeight: '500',
                                transition: 'all 0.2s ease',
                                flexShrink: 0,
                                whiteSpace: 'nowrap',
                                width: 'auto',
                                height: 'auto',
                                lineHeight: '1.2'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.background = '#DC2626';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.background = '#EF4444';
                              }}
                            >
                              Remove
                            </button>
                          </div>
                        )}
                        <small style={{ 
                          display: 'block', 
                          marginTop: '0.5rem', 
                          color: '#6B7280', 
                          fontSize: '0.85rem',
                          fontStyle: 'italic'
                        }}>
                          Max size: 5MB. Accepted formats: JPG, PNG, GIF
                        </small>
                      </div>
                      
                      <div className="form-group" style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                        <label htmlFor="nationalId" style={{ marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
                          National/Government ID
                        </label>
                        <input
                          type="text"
                          id="nationalId"
                          name="nationalId"
                          value={teacherForm.nationalId}
                          onChange={handleInputChange}
                          className={formErrors.nationalId ? 'error' : ''}
                          placeholder="Enter National/Government ID (optional)"
                          style={{
                            width: '100%',
                            boxSizing: 'border-box'
                          }}
                        />
                        {formErrors.nationalId && (
                          <span className="error-text">{formErrors.nationalId}</span>
                        )}
                      </div>
                      
                      <div className="form-group" style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                        <label htmlFor="employeeId" style={{ marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
                          Employee ID
                        </label>
                        <input
                          type="text"
                          id="employeeId"
                          name="employeeId"
                          value={teacherForm.employeeId}
                          onChange={handleInputChange}
                          placeholder="Enter Employee ID (optional)"
                          style={{
                            width: '100%',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>
                    </div>
                    
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="email">Email *</label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={teacherForm.email}
                          onChange={handleInputChange}
                          className={formErrors.email ? 'error' : ''}
                          placeholder="teacher@example.com"
                        />
                        {formErrors.email && (
                          <span className="error-text">{formErrors.email}</span>
                        )}
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="dateOfBirth">Date of Birth *</label>
                        <input
                          type="date"
                          id="dateOfBirth"
                          name="dateOfBirth"
                          value={teacherForm.dateOfBirth}
                          onChange={handleInputChange}
                          className={formErrors.dateOfBirth ? 'error' : ''}
                        />
                        {formErrors.dateOfBirth && (
                          <span className="error-text">{formErrors.dateOfBirth}</span>
                        )}
                      </div>
                    </div>
                    
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="phone">Phone *</label>
                        <div className="phone-input-wrapper">
                          <select
                            className="country-code-select"
                            value={teacherPhoneCountryCode}
                            onChange={(e) => setTeacherPhoneCountryCode(e.target.value)}
                          >
                            <option value="+1">🇺🇸 +1 (US/CA)</option>
                            <option value="+44">🇬🇧 +44 (UK)</option>
                            <option value="+91">🇮🇳 +91 (India)</option>
                            <option value="+92">🇵🇰 +92 (Pakistan)</option>
                            <option value="+971">🇦🇪 +971 (UAE)</option>
                            <option value="+966">🇸🇦 +966 (Saudi Arabia)</option>
                            <option value="+974">🇶🇦 +974 (Qatar)</option>
                            <option value="+965">🇰🇼 +965 (Kuwait)</option>
                            <option value="+973">🇧🇭 +973 (Bahrain)</option>
                            <option value="+968">🇴🇲 +968 (Oman)</option>
                            <option value="+961">🇱🇧 +961 (Lebanon)</option>
                            <option value="+962">🇯🇴 +962 (Jordan)</option>
                            <option value="+20">🇪🇬 +20 (Egypt)</option>
                            <option value="+27">🇿🇦 +27 (South Africa)</option>
                            <option value="+61">🇦🇺 +61 (Australia)</option>
                            <option value="+64">🇳🇿 +64 (New Zealand)</option>
                            <option value="+86">🇨🇳 +86 (China)</option>
                            <option value="+81">🇯🇵 +81 (Japan)</option>
                            <option value="+82">🇰🇷 +82 (South Korea)</option>
                            <option value="+65">🇸🇬 +65 (Singapore)</option>
                            <option value="+60">🇲🇾 +60 (Malaysia)</option>
                            <option value="+66">🇹🇭 +66 (Thailand)</option>
                            <option value="+62">🇮🇩 +62 (Indonesia)</option>
                            <option value="+63">🇵🇭 +63 (Philippines)</option>
                            <option value="+84">🇻🇳 +84 (Vietnam)</option>
                            <option value="+880">🇧🇩 +880 (Bangladesh)</option>
                            <option value="+94">🇱🇰 +94 (Sri Lanka)</option>
                            <option value="+977">🇳🇵 +977 (Nepal)</option>
                            <option value="+33">🇫🇷 +33 (France)</option>
                            <option value="+49">🇩🇪 +49 (Germany)</option>
                            <option value="+39">🇮🇹 +39 (Italy)</option>
                            <option value="+34">🇪🇸 +34 (Spain)</option>
                            <option value="+31">🇳🇱 +31 (Netherlands)</option>
                            <option value="+32">🇧🇪 +32 (Belgium)</option>
                            <option value="+41">🇨🇭 +41 (Switzerland)</option>
                            <option value="+43">🇦🇹 +43 (Austria)</option>
                            <option value="+46">🇸🇪 +46 (Sweden)</option>
                            <option value="+47">🇳🇴 +47 (Norway)</option>
                            <option value="+45">🇩🇰 +45 (Denmark)</option>
                            <option value="+358">🇫🇮 +358 (Finland)</option>
                            <option value="+7">🇷🇺 +7 (Russia)</option>
                            <option value="+90">🇹🇷 +90 (Turkey)</option>
                            <option value="+52">🇲🇽 +52 (Mexico)</option>
                            <option value="+55">🇧🇷 +55 (Brazil)</option>
                            <option value="+54">🇦🇷 +54 (Argentina)</option>
                            <option value="+57">🇨🇴 +57 (Colombia)</option>
                            <option value="+234">🇳🇬 +234 (Nigeria)</option>
                            <option value="+254">🇰🇪 +254 (Kenya)</option>
                            <option value="+212">🇲🇦 +212 (Morocco)</option>
                            <option value="+972">🇮🇱 +972 (Israel)</option>
                            <option value="+98">🇮🇷 +98 (Iran)</option>
                            <option value="+964">🇮🇶 +964 (Iraq)</option>
                            <option value="+967">🇾🇪 +967 (Yemen)</option>
                            <option value="+963">🇸🇾 +963 (Syria)</option>
                          </select>
                          <input
                            type="tel"
                            id="phone"
                            name="phone"
                            value={teacherForm.phone}
                            onChange={handleInputChange}
                            className={formErrors.phone ? 'error' : ''}
                            placeholder="e.g. 300 1234567"
                          />
                        </div>
                        {formErrors.phone && (
                          <span className="error-text">{formErrors.phone}</span>
                        )}
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="address">Address *</label>
                        <input
                          type="text"
                          id="address"
                          name="address"
                          value={teacherForm.address}
                          onChange={handleInputChange}
                          className={formErrors.address ? 'error' : ''}
                          placeholder="Enter street address"
                        />
                        {formErrors.address && (
                          <span className="error-text">{formErrors.address}</span>
                        )}
                      </div>
                    </div>
                    
                    <div className="form-row three-columns">
                      <div className="form-group">
                        <label htmlFor="country">Country *</label>
                        <input
                          type="text"
                          id="country"
                          name="country"
                          value={teacherForm.country}
                          onChange={handleInputChange}
                          className={formErrors.country ? 'error' : ''}
                          placeholder="Enter country"
                        />
                        {formErrors.country && (
                          <span className="error-text">{formErrors.country}</span>
                        )}
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="city">City / Town *</label>
                        <input
                          type="text"
                          id="city"
                          name="city"
                          value={teacherForm.city}
                          onChange={handleInputChange}
                          className={formErrors.city ? 'error' : ''}
                          placeholder="Enter city or town"
                        />
                        {formErrors.city && (
                          <span className="error-text">{formErrors.city}</span>
                        )}
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="postalCode">Postal Code *</label>
                        <input
                          type="text"
                          id="postalCode"
                          name="postalCode"
                          value={teacherForm.postalCode}
                          onChange={handleInputChange}
                          className={formErrors.postalCode ? 'error' : ''}
                          placeholder="Enter postal code"
                        />
                        {formErrors.postalCode && (
                          <span className="error-text">{formErrors.postalCode}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="form-section">
                    <h3>Professional Information</h3>
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="qualification">Qualification *</label>
                        <input
                          type="text"
                          id="qualification"
                          name="qualification"
                          value={teacherForm.qualification}
                          onChange={handleInputChange}
                          className={formErrors.qualification ? 'error' : ''}
                          placeholder="e.g., B.Ed, M.A, Ph.D"
                        />
                        {formErrors.qualification && (
                          <span className="error-text">{formErrors.qualification}</span>
                        )}
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="experience">Experience (Years) *</label>
                        <input
                          type="number"
                          id="experience"
                          name="experience"
                          value={teacherForm.experience}
                          onChange={handleInputChange}
                          className={formErrors.experience ? 'error' : ''}
                          placeholder="e.g., 5"
                          min="0"
                        />
                        {formErrors.experience && (
                          <span className="error-text">{formErrors.experience}</span>
                        )}
                      </div>
                    </div>
                    
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="salary">Salary *</label>
                        <input
                          type="number"
                          id="salary"
                          name="salary"
                          value={teacherForm.salary}
                          onChange={handleInputChange}
                          className={formErrors.salary ? 'error' : ''}
                          placeholder="e.g., 50000"
                          min="0"
                          step="0.01"
                        />
                        {formErrors.salary && (
                          <span className="error-text">{formErrors.salary}</span>
                        )}
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="joiningDate">Joining Date *</label>
                        <input
                          type="date"
                          id="joiningDate"
                          name="joiningDate"
                          value={teacherForm.joiningDate}
                          onChange={handleInputChange}
                          className={formErrors.joiningDate ? 'error' : ''}
                        />
                        {formErrors.joiningDate && (
                          <span className="error-text">{formErrors.joiningDate}</span>
                        )}
                      </div>
                    </div>
                    
                    <div className="form-section" style={{
                      width: '100%',
                      maxWidth: '100%',
                      boxSizing: 'border-box',
                      overflowX: 'hidden',
                      padding: '1rem'
                    }}>
                      <div className="subjects-header" style={{
                        width: '100%',
                        maxWidth: '100%',
                        boxSizing: 'border-box',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'stretch',
                        gap: '1rem',
                        padding: '1rem',
                        overflowX: 'visible'
                      }}>
                        <h4>Subjects *</h4>
                        <button 
                          type="button" 
                          className="add-subject-btn"
                          onClick={addSubject}
                          style={{
                            width: '100%',
                            maxWidth: '100%',
                            boxSizing: 'border-box',
                            padding: '0.75rem 1rem',
                            fontSize: '0.95rem',
                            whiteSpace: 'normal',
                            wordWrap: 'break-word'
                          }}
                        >
                          + Add Subject
                        </button>
                      </div>
                      
                      {formErrors.subjects && (
                        <span className="error-text">{formErrors.subjects}</span>
                      )}
                      
                      <div className="subjects-list" style={{ 
                        width: '100%', 
                        maxWidth: '100%', 
                        boxSizing: 'border-box',
                        overflowX: 'hidden',
                        padding: '1rem'
                      }}>
                        {teacherForm.subjects.map((subject, index) => (
                          <div 
                            key={index} 
                            className="subject-item"
                            style={{
                              width: '100%',
                              maxWidth: '100%',
                              boxSizing: 'border-box',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'stretch',
                              gap: '1rem',
                              padding: '1rem',
                              overflowX: 'hidden'
                            }}
                          >
                            <select
                              value={subject}
                              onChange={(e) => handleSubjectChange(index, e.target.value)}
                              className={formErrors[`subject_${index}`] ? 'error' : ''}
                              style={{
                                width: '100%',
                                maxWidth: '100%',
                                boxSizing: 'border-box',
                                padding: '0.875rem 1rem',
                                fontSize: '1rem',
                                minWidth: 0,
                                flex: 'none',
                                borderRadius: '6px',
                                border: formErrors[`subject_${index}`] ? '1px solid #EF4444' : '1px solid #D1D5DB',
                                backgroundColor: '#FFFFFF'
                              }}
                            >
                              <option value="">-- Select Subject --</option>
                              {loadingSubjects ? (
                                <option disabled>Loading subjects...</option>
                              ) : availableSubjects.length > 0 ? (
                                availableSubjects.map((subjectName, idx) => (
                                  <option key={idx} value={subjectName}>
                                    {subjectName}
                                  </option>
                                ))
                              ) : (
                                <option disabled>No subjects found. Add subjects in Curriculum Form first.</option>
                              )}
                            </select>
                            {teacherForm.subjects.length > 1 && (
                              <button
                                type="button"
                                className="remove-subject-btn"
                                onClick={() => removeSubject(index)}
                                title="Remove subject"
                                style={{
                                  width: '100%',
                                  maxWidth: '100%',
                                  boxSizing: 'border-box',
                                  padding: '0.75rem 1rem',
                                  fontSize: '1rem',
                                  marginTop: '0.5rem'
                                }}
                              >
                                ×
                              </button>
                            )}
                            {formErrors[`subject_${index}`] && (
                              <span className="error-text">{formErrors[`subject_${index}`]}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="form-section">
                    <h3>Teachers Password</h3>
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="password">Password *</label>
                        <div className="password-input-wrapper">
                          <input
                            type={showPassword ? "text" : "password"}
                            id="password"
                            name="password"
                            value={teacherForm.password}
                            onChange={handleInputChange}
                            className={formErrors.password ? 'error' : ''}
                            placeholder="Enter password"
                          />
                          <span 
                            className="password-toggle-icon"
                            onClick={() => setShowPassword(!showPassword)}
                            title={showPassword ? "Hide password" : "Show password"}
                          >
                            {showPassword ? '🙈' : '👁️'}
                          </span>
                        </div>
                        {formErrors.password && (
                          <span className="error-text">{formErrors.password}</span>
                        )}
                        <div style={{ marginTop: '0.5rem' }}>
                          <button
                            type="button"
                            className="toggle-btn"
                            onClick={() => {
                              const genPassword = generatePassword();
                              setTeacherForm(prev => ({
                                ...prev,
                                password: genPassword,
                                confirmPassword: genPassword
                              }));
                            }}
                            style={{
                              padding: '0.4rem 0.8rem',
                              backgroundColor: '#10B981',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontWeight: '500',
                              fontSize: '0.85rem'
                            }}
                          >
                            Generate Password
                          </button>
                        </div>
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="confirmPassword">Confirm Password *</label>
                        <div className="password-input-wrapper">
                          <input
                            type={showConfirmPassword ? "text" : "password"}
                            id="confirmPassword"
                            name="confirmPassword"
                            value={teacherForm.confirmPassword}
                            onChange={handleInputChange}
                            className={formErrors.confirmPassword ? 'error' : ''}
                            placeholder="Confirm password"
                          />
                          <span 
                            className="password-toggle-icon"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            title={showConfirmPassword ? "Hide password" : "Show password"}
                          >
                            {showConfirmPassword ? '🙈' : '👁️'}
                          </span>
                        </div>
                        {formErrors.confirmPassword && (
                          <span className="error-text">{formErrors.confirmPassword}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="form-section">
                    <h3>Emergency Contact</h3>
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="emergencyContact">Emergency Contact *</label>
                        <input
                          type="text"
                          id="emergencyContact"
                          name="emergencyContact"
                          value={teacherForm.emergencyContact}
                          onChange={handleInputChange}
                          className={formErrors.emergencyContact ? 'error' : ''}
                          placeholder="Emergency contact name"
                        />
                        {formErrors.emergencyContact && (
                          <span className="error-text">{formErrors.emergencyContact}</span>
                        )}
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="emergencyPhone">Emergency Phone *</label>
                        <div className="phone-input-wrapper">
                          <select
                            className="country-code-select"
                            value={teacherEmergencyPhoneCountryCode}
                            onChange={(e) => setTeacherEmergencyPhoneCountryCode(e.target.value)}
                          >
                            <option value="+1">🇺🇸 +1 (US/CA)</option>
                            <option value="+44">🇬🇧 +44 (UK)</option>
                            <option value="+91">🇮🇳 +91 (India)</option>
                            <option value="+92">🇵🇰 +92 (Pakistan)</option>
                            <option value="+971">🇦🇪 +971 (UAE)</option>
                            <option value="+966">🇸🇦 +966 (Saudi Arabia)</option>
                            <option value="+974">🇶🇦 +974 (Qatar)</option>
                            <option value="+965">🇰🇼 +965 (Kuwait)</option>
                            <option value="+973">🇧🇭 +973 (Bahrain)</option>
                            <option value="+968">🇴🇲 +968 (Oman)</option>
                            <option value="+961">🇱🇧 +961 (Lebanon)</option>
                            <option value="+962">🇯🇴 +962 (Jordan)</option>
                            <option value="+20">🇪🇬 +20 (Egypt)</option>
                            <option value="+27">🇿🇦 +27 (South Africa)</option>
                            <option value="+61">🇦🇺 +61 (Australia)</option>
                            <option value="+64">🇳🇿 +64 (New Zealand)</option>
                            <option value="+86">🇨🇳 +86 (China)</option>
                            <option value="+81">🇯🇵 +81 (Japan)</option>
                            <option value="+82">🇰🇷 +82 (South Korea)</option>
                            <option value="+65">🇸🇬 +65 (Singapore)</option>
                            <option value="+60">🇲🇾 +60 (Malaysia)</option>
                            <option value="+66">🇹🇭 +66 (Thailand)</option>
                            <option value="+62">🇮🇩 +62 (Indonesia)</option>
                            <option value="+63">🇵🇭 +63 (Philippines)</option>
                            <option value="+84">🇻🇳 +84 (Vietnam)</option>
                            <option value="+880">🇧🇩 +880 (Bangladesh)</option>
                            <option value="+94">🇱🇰 +94 (Sri Lanka)</option>
                            <option value="+977">🇳🇵 +977 (Nepal)</option>
                            <option value="+33">🇫🇷 +33 (France)</option>
                            <option value="+49">🇩🇪 +49 (Germany)</option>
                            <option value="+39">🇮🇹 +39 (Italy)</option>
                            <option value="+34">🇪🇸 +34 (Spain)</option>
                            <option value="+31">🇳🇱 +31 (Netherlands)</option>
                            <option value="+32">🇧🇪 +32 (Belgium)</option>
                            <option value="+41">🇨🇭 +41 (Switzerland)</option>
                            <option value="+43">🇦🇹 +43 (Austria)</option>
                            <option value="+46">🇸🇪 +46 (Sweden)</option>
                            <option value="+47">🇳🇴 +47 (Norway)</option>
                            <option value="+45">🇩🇰 +45 (Denmark)</option>
                            <option value="+358">🇫🇮 +358 (Finland)</option>
                            <option value="+7">🇷🇺 +7 (Russia)</option>
                            <option value="+90">🇹🇷 +90 (Turkey)</option>
                            <option value="+52">🇲🇽 +52 (Mexico)</option>
                            <option value="+55">🇧🇷 +55 (Brazil)</option>
                            <option value="+54">🇦🇷 +54 (Argentina)</option>
                            <option value="+57">🇨🇴 +57 (Colombia)</option>
                            <option value="+234">🇳🇬 +234 (Nigeria)</option>
                            <option value="+254">🇰🇪 +254 (Kenya)</option>
                            <option value="+212">🇲🇦 +212 (Morocco)</option>
                            <option value="+972">🇮🇱 +972 (Israel)</option>
                            <option value="+98">🇮🇷 +98 (Iran)</option>
                            <option value="+964">🇮🇶 +964 (Iraq)</option>
                            <option value="+967">🇾🇪 +967 (Yemen)</option>
                            <option value="+963">🇸🇾 +963 (Syria)</option>
                          </select>
                          <input
                            type="tel"
                            id="emergencyPhone"
                            name="emergencyPhone"
                            value={teacherForm.emergencyPhone}
                            onChange={handleInputChange}
                            className={formErrors.emergencyPhone ? 'error' : ''}
                            placeholder="e.g. 300 1234567"
                          />
                        </div>
                        {formErrors.emergencyPhone && (
                          <span className="error-text">{formErrors.emergencyPhone}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="form-actions">
                    <button 
                      type="button" 
                      className="cancel-btn"
                      onClick={() => navigate('/school/dashboard')}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit" 
                      className="submit-btn"
                      disabled={isSubmitting || uploadingPicture}
                    >
                      {uploadingPicture ? 'Uploading Picture...' : isSubmitting ? 'Adding Teacher...' : 'Add Teacher'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Success Popup */}
      {showSuccessPopup && successData && (
        <div 
          className="popup-overlay" 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
          }}
          onClick={() => {
            setShowSuccessPopup(false);
            setSuccessData(null);
          }}
        >
          <div 
            style={{
              background: '#FFFFFF',
              borderRadius: '16px',
              padding: '2rem',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              position: 'relative',
              animation: 'slideIn 0.3s ease-out'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Success Icon */}
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem',
              boxShadow: '0 10px 15px -3px rgba(16, 185, 129, 0.3)'
            }}>
              <span style={{ fontSize: '3rem', color: '#FFFFFF' }}>✓</span>
            </div>

            {/* Title */}
            <h2 style={{
              textAlign: 'center',
              color: '#1F2937',
              fontSize: '1.5rem',
              fontWeight: '700',
              marginBottom: '0.5rem'
            }}>
              Teacher Added Successfully!
            </h2>

            {/* Subtitle */}
            <p style={{
              textAlign: 'center',
              color: '#6B7280',
              fontSize: '0.95rem',
              marginBottom: '2rem'
            }}>
              {successData.name} has been added to the system
            </p>

            {/* Credentials Card */}
            <div style={{
              background: '#F9FAFB',
              borderRadius: '12px',
              padding: '1.5rem',
              marginBottom: '1.5rem',
              border: '2px solid #E5E7EB'
            }}>
              <h3 style={{
                color: '#374151',
                fontSize: '1rem',
                fontWeight: '600',
                marginBottom: '1rem',
                textAlign: 'center'
              }}>
                Login Credentials
              </h3>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {/* Email */}
                <div style={{
                  background: '#FFFFFF',
                  borderRadius: '8px',
                  padding: '1rem',
                  border: '1px solid #E5E7EB'
                }}>
                  <div style={{
                    fontSize: '0.75rem',
                    color: '#6B7280',
                    fontWeight: '500',
                    marginBottom: '0.5rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Email
                  </div>
                  <div style={{
                    fontSize: '1rem',
                    color: '#1F2937',
                    fontWeight: '600',
                    wordBreak: 'break-all'
                  }}>
                    {successData.email}
                  </div>
                </div>

                {/* Password */}
                <div style={{
                  background: '#FFFFFF',
                  borderRadius: '8px',
                  padding: '1rem',
                  border: '1px solid #E5E7EB'
                }}>
                  <div style={{
                    fontSize: '0.75rem',
                    color: '#6B7280',
                    fontWeight: '500',
                    marginBottom: '0.5rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Password
                  </div>
                  <div style={{
                    fontSize: '1rem',
                    color: '#1F2937',
                    fontWeight: '600',
                    fontFamily: 'monospace',
                    letterSpacing: '1px'
                  }}>
                    {successData.password}
                  </div>
                </div>
              </div>
            </div>

            {/* Info Message */}
            <div style={{
              background: '#EFF6FF',
              borderRadius: '8px',
              padding: '1rem',
              marginBottom: '1.5rem',
              border: '1px solid #BFDBFE'
            }}>
              <p style={{
                color: '#1E40AF',
                fontSize: '0.875rem',
                margin: 0,
                textAlign: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}>
                <span>ℹ️</span>
                <span>Please share these credentials with the teacher</span>
              </p>
            </div>

            {/* Close Button */}
            <button
              onClick={() => {
                setShowSuccessPopup(false);
                setSuccessData(null);
              }}
              style={{
                width: '100%',
                padding: '0.875rem 1.5rem',
                background: '#FF8C42',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#FFA366';
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 4px 8px rgba(255, 140, 66, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = '#FF8C42';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
              }}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default AddTeacher;
